// Le GPU du visiteur, d'après le nom que donne son navigateur (WebGL, extension WEBGL_debug_renderer_info :
// « ANGLE (NVIDIA, NVIDIA GeForce RTX 4090 (0x00002684) Direct3D11 vs_5_0 ps_5_0, D3D11) », « ANGLE (Apple,
// ANGLE Metal Renderer: Apple M5 Max, Unspecified Version) »). Lu dans la page, seulement quand le visiteur
// le demande, jamais envoyé ni gardé : ce nom sert aussi au pistage, d'où les navigateurs qui le masquent
// (Brave, Safari : « Apple GPU », Firefox en mode protégé). Firefox, lui, remplace le nom par un modèle
// générique suivi de « , or similar » (dom/canvas/SanitizeRenderer.cpp) : toute puce Apple devient
// « Apple M1, or similar », presque toute carte NVIDIA récente « GeForce GTX 980, or similar ». On n'en
// garde que la famille. Ici, la correspondance seule, sans navigateur.

/** Ce que le nom permet de dire : un GPU de la liste ; une puce Apple et ses variantes (la mémoire unifiée
 *  ne se lit pas : le visiteur choisit) ; un GPU reconnu mais absent de la liste ; rien. */
export type Detected =
  | { kind: "gpu"; id: string; name: string }
  /** Puce dont le navigateur ne dit pas la mémoire (Apple, AMD Ryzen AI Max) : ses versions, de la plus grande à la plus petite. */
  | { kind: "memory"; chip: string; ids: string[] }
  | { kind: "unlisted"; name: string }
  | { kind: "none"; name: string }
  /** Nom générique de Firefox : la famille seule (« Apple Silicon », « NVIDIA »), pas le modèle. */
  | { kind: "family"; family: string };

/** Du plus précis au moins précis : « RTX 4090 Laptop » avant « RTX 4090 », « 4070 Ti SUPER » avant « 4070 ». */
const RULES: [RegExp, string][] = [
  [/rtx 5090 laptop/, "rtx-5090-laptop"], [/rtx 5090\b/, "rtx-5090"],
  [/rtx 5080 laptop/, "rtx-5080-laptop"], [/rtx 5080\b/, "rtx-5080"],
  [/rtx 5070 ti laptop/, "rtx-5070-ti-laptop"], [/rtx 5070 ti\b/, "rtx-5070-ti"],
  [/rtx 5070 laptop/, "rtx-5070-laptop"], [/rtx 5070\b(?! ti)/, "rtx-5070"],
  [/rtx 5060 ti\b/, "rtx-5060-ti"], [/rtx 5060\b(?! ti| laptop)/, "rtx-5060"],
  [/rtx 4090 laptop/, "rtx-4090-laptop"], [/rtx 4090 ?d\b/, "rtx-4090-d"], [/rtx 4090\b/, "rtx-4090"],
  [/rtx 4080 laptop/, "rtx-4080-laptop"], [/rtx 4080\b/, "rtx-4080"],
  [/rtx 4070 ti super/, "rtx-4070-ti-super"], [/rtx 4070 laptop/, "rtx-4070-laptop"], [/rtx 4070\b(?! ti)/, "rtx-4070"],
  [/rtx 4060 ti\b/, "rtx-4060-ti"], [/rtx 4060 laptop/, "rtx-4060-laptop"], [/rtx 4060\b(?! ti)/, "rtx-4060"],
  [/rtx 3090 ti\b/, "rtx-3090-ti"], [/rtx 3090\b(?! ti)/, "rtx-3090"], [/rtx 3080\b(?! ti| laptop)/, "rtx-3080"], [/rtx 3060\b(?! ti| laptop)/, "rtx-3060"],
  [/rtx pro 6000/, "rtx-pro-6000"], [/rtx pro 4500/, "rtx-pro-4500"], [/rtx pro 4000/, "rtx-pro-4000"],
  [/rtx 6000 ada/, "rtx-6000-ada"], [/rtx 4000 ada/, "rtx-4000-ada"], [/rtx 2000 ada/, "rtx-2000-ada"],
  [/rtx a6000/, "rtx-a6000"], [/rtx a5000/, "rtx-a5000"], [/rtx a4500/, "rtx-a4500"], [/rtx a4000/, "rtx-a4000"],
  [/\bl40s\b/, "l40s"], [/\bl40\b/, "l40"], [/\ba40\b/, "a40"], [/\bl4\b/, "l4"], [/h100 nvl/, "h100-nvl"], [/h100 pcie/, "h100-pcie"], [/\bh100\b/, "h100"],
  [/\bh200\b/, "h200"], [/a100.*80\s?gb/, "a100-80"], [/\ba100\b/, "a100-40"], [/dgx spark|\bgb10\b/, "dgx-spark"],
  [/rx 7900 xtx/, "rx-7900-xtx"], [/rx 9070 xt/, "rx-9070-xt"], [/ai pro r9700/, "radeon-ai-pro-r9700"],
  [/arc\s*(\(tm\))?\s*pro b70/, "arc-pro-b70"], [/arc\s*(\(tm\))?\s*pro b60/, "arc-pro-b60"], [/arc\s*(\(tm\))?\s*b580/, "arc-b580"],
];
/** AMD Ryzen AI Max, par GPU intégré : nom à afficher, versions de la liste. La 8050S équipe aussi les PRO 490 et 485
 *  (série 400, 273 Go/s) : seule leur version 192 Go est proposée, celle de 128 Go diffère à peine des 390 / 385. */
const RYZEN_AI_MAX: Record<string, [string, string[]]> = {
  "8065": ["AMD Ryzen AI Max+ PRO 495 (Radeon 8065S)", ["ryzen-ai-max-pro-495-192", "ryzen-ai-max-pro-495-128"]],
  "8060": ["AMD Ryzen AI Max+ 395 / 392 / 388 (Radeon 8060S)", ["strix-halo", "ryzen-ai-max-395-64", "ryzen-ai-max-395-32"]],
  "8050": ["AMD Ryzen AI Max 390 / 385 or PRO 490 / 485 (Radeon 8050S)", ["ryzen-ai-max-pro-490-192", "ryzen-ai-max-390-128", "ryzen-ai-max-390-64", "ryzen-ai-max-390-32"]],
  "8040": ["AMD Ryzen AI Max PRO 380 (Radeon 8040S)", ["ryzen-ai-max-pro-380-64", "ryzen-ai-max-pro-380-32"]],
};
/** Puces Apple de la liste : la même puce existe en plusieurs mémoires, de la plus grande à la plus petite. */
const APPLE: Record<string, string[]> = {
  "m5 ultra": ["mac-m5-ultra-512", "mac-m5-ultra-256", "mac-m5-ultra-96"], "m5 max": ["mac-m5-max-128", "mac-m5-max-64", "mac-m5-max-36"],
  "m5 pro": ["mac-m5-pro-64", "mac-m5-pro-48", "mac-m5-pro-24"], m5: ["mac-m5-32", "mac-m5-16"],
  "m4 max": ["mac-m4-max-128", "mac-m4-max-64", "mac-m4-max-48", "mac-m4-max-36"], "m4 pro": ["mac-m4-pro-64", "mac-m4-pro-48", "mac-m4-pro-24"],
  m4: ["mac-m4-32", "mac-m4-24", "mac-m4-16"],
  "m3 ultra": ["mac-m3-ultra-512", "mac-m3-ultra-256", "mac-m3-ultra-96"], "m3 max": ["mac-m3-max-128", "mac-m3-max-96", "mac-m3-max-64", "mac-m3-max-48", "mac-m3-max-36"],
  "m3 pro": ["mac-m3-pro-36", "mac-m3-pro-18"], m3: ["mac-m3-24", "mac-m3-16", "mac-m3-8"],
  "m2 ultra": ["mac-m2-ultra-192", "mac-m2-ultra-128", "mac-m2-ultra-64"], "m2 max": ["mac-m2-max-96", "mac-m2-max-64", "mac-m2-max-32"],
  "m2 pro": ["mac-m2-pro-32", "mac-m2-pro-16"], m2: ["mac-m2-24", "mac-m2-16", "mac-m2-8"],
  "m1 ultra": ["mac-m1-ultra-128", "mac-m1-ultra-64"], "m1 max": ["mac-m1-max-64", "mac-m1-max-32"], "m1 pro": ["mac-m1-pro-32", "mac-m1-pro-16"],
  m1: ["mac-m1-16", "mac-m1-8"],
};

/** Le nom du GPU dans la chaîne du navigateur : l'intérieur de « ANGLE (vendeur, nom, pilote) », sans
 *  identifiant matériel ni API graphique. */
export function rendererName(renderer: string): string {
  const angle = /^ANGLE \(([^,]+),\s*(.+?)(?:,\s*[^,]*)?\)$/.exec(renderer.trim());
  const name = (angle ? angle[2] : renderer)
    .replace(/ANGLE Metal Renderer:\s*/i, "")
    .replace(/\s*\(0x[0-9a-f]+\)/i, "")
    .replace(/\s+(Direct3D|D3D|OpenGL|Vulkan|vs_|ps_)\S*.*$/i, "")
    .replace(/\/PCIe\/SSE2.*$/i, "")
    .trim();
  return name;
}

/** Familles que Firefox laisse deviner derrière son nom générique. */
const FAMILIES: [RegExp, string][] = [[/apple/i, "Apple Silicon"], [/nvidia|geforce|quadro/i, "NVIDIA"], [/amd|radeon/i, "AMD"], [/intel/i, "Intel"]];

export function detectGpu(renderer: string): Detected {
  if (/,\s*or similar\)?\s*$/i.test(renderer.trim())) {
    return { kind: "family", family: FAMILIES.find(([re]) => re.test(renderer))?.[1] ?? "GPU" };
  }
  const name = rendererName(renderer);
  const s = name.toLowerCase().replace(/geforce|nvidia|amd|radeon(?!\s*(\(tm\))?\s*8060s)|intel\(r\)|\(r\)|\(tm\)|generation|blackwell|graphics/g, " ").replace(/\s+/g, " ").trim();
  const apple = /apple (m\d)(?: (pro|max|ultra))?\b/i.exec(name);
  if (apple) {
    const chip = `${apple[1]}${apple[2] ? ` ${apple[2]}` : ""}`.toLowerCase();
    const label = `Apple ${chip.replace(/\b\w/g, (c) => c.toUpperCase())}`; // « Apple M2 Pro »
    return APPLE[chip] ? { kind: "memory", chip: label, ids: APPLE[chip] } : { kind: "unlisted", name: label };
  }
  // AMD Ryzen AI Max : le GPU intégré nomme la famille, pas la mémoire installée.
  const radeon = /radeon\s*(?:\(tm\))?\s*(80[4-6]\d)s/i.exec(name) ?? (/ryzen\s*(?:\(tm\))?\s*ai max/i.test(name) ? [name, "8060"] : null);
  if (radeon && RYZEN_AI_MAX[radeon[1]]) return { kind: "memory", chip: RYZEN_AI_MAX[radeon[1]][0], ids: RYZEN_AI_MAX[radeon[1]][1] };
  const rule = RULES.find(([re]) => re.test(s) || re.test(name.toLowerCase()));
  if (rule) return { kind: "gpu", id: rule[1], name };
  // Une carte graphique dédiée reconnue (GeForce, Radeon RX, Arc), mais pas dans la liste.
  if (/geforce|rtx|gtx|radeon rx|radeon pro|\barc\b|quadro|tesla|instinct/i.test(name)) return { kind: "unlisted", name };
  return { kind: "none", name };
}
